<template>
  <v-dialog
    :content-class="contentClass"
    v-model="internalShow"
    :max-width="maxWidth"
    v-bind="$attrs"
    :persistent="persistent"
    @keydown.esc="close()"
    @click:outside="close()"
    :scrollable="false"
  >
    <template v-if="$scopedSlots.activator && activator" v-slot:activator="{ on }">
      <slot name="activator" :on="on"/>
    </template>

    <div v-if="internalShow" :data-attr="dataAttr">
      <v-toolbar v-if="title" color="primary">

        <h2>{{ title }}</h2>

        <v-spacer />
        <v-tooltip v-if="canBeFullScreen" bottom>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" small icon :ripple="false" @click="fullcreen()">
              <i :class="`sr-icon-${fullContainerView ? 'collapse-full' : 'expand-full'}`"></i>
            </v-btn>
          </template>
          <span>Full Screen</span>
        </v-tooltip>
        <v-btn small icon @click="close()" :ripple="false" :disabled="loading">
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-btn v-else-if="closable" small icon @click="close()" :ripple="false" :disabled="loading" class="close-btn">
        <v-icon>close</v-icon>
      </v-btn>

      <v-progress-linear class="dialog-form-progress" indeterminate :active="loading" />

      <div class="dialog-content">
        <v-form ref="form">
          <div :class="{ fullView : fullContainerView }">
            <slot v-if="$scopedSlots.default" :formData="formData" />
            <slot v-else-if="$scopedSlots.form" name="form" :formData="formData" />
            <slot v-else />

            <v-row class="unit dialog-form-actions" v-if="showActions">
              <slot v-if="$slots.actions" name="actions" />
              <template v-else>
                <v-col cols="auto">
                  <v-btn @click="doSubmit()" :loading="loading" rounded color="primary">{{ btnSubmit }}</v-btn>
                </v-col>

                <v-col cols="auto">
                  <v-btn @click="close()" text :disabled="loading" rounded>{{ btnCancel }}</v-btn>
                </v-col>
              </template>
            </v-row>
          </div>
        </v-form>
      </div>
    </div>

  </v-dialog>
</template>

<script>
  import * as Sentry from "@sentry/vue";

  export default {
    name: "DialogForm",

    props: {
      initialData: { type: Object, default: () => ({}) },
      activator: { type: Boolean, default: true },
      title: { type: String },
      buttonSubmit: { type: String },
      buttonCancel: { type: String },
      maxWidth: { type: [String, Number], default: 800 },
      submit: { type: Function, default: () => () => {} },
      show: { type: Boolean, default: false },
      showActions: { type: Boolean, default: false },
      canBeFullScreen: { type: Boolean, default: false},
      fullContainerView: { type: Boolean, default: false},
      closable: { type: Boolean, default: false},
      persistent: { type: Boolean, default: false},
      dataAttr: { type: String },
    },

    data() {
      return {
        internalShow: this.show,
        loading: false,
        formData: { ...this.initialData },
        help: false,
      };
    },

    computed: {
      btnSubmit() {
        return this.buttonSubmit || this.$t('actions.submit');
      },
      btnCancel() {
        return this.buttonCancel || this.$t('actions.cancel');
      },
      contentClass() {
        // eslint-disable-next-line
        return 'dialog-form ' + (this.$attrs['content-class'] || this.$attrs['contentClass']);
      },
    },

    methods: {
      async doSubmit() {
        if (!this.validate()) return;

        try {
          this.loading = true;
          const result = await this.submit(this.formData);
          this.$emit('confirm', result);
          this.close();
          this.$emit('success', result);
        } catch (error) {
          Sentry.captureException(error);
          this.$emit('error', error);
        } finally {
          this.loading = false;
        }
      },
      reset() {
        this.$refs.form.reset();
        this.$nextTick(_ => {
          this.formData = { ...this.initialData };
        });
      },
      close() {
        if(this.persistent) return;
        this.reset();
        this.$emit('close');
        this.internalShow = false;
      },
      fullcreen() {
        this.$emit('fullscreen');
      },
      validate() {
        return this.$refs.form.validate();
      },
    },
    watch: {
      show() {
        this.internalShow = this.show;
      },
      initialData: {
        handler() {
          this.formData = { ...this.initialData };
        },
        deep: true,
      },
    },
  };
</script>

<style lang="scss">
  .v-sheet.v-toolbar {
    padding: 22px 24px;
    color: $white;

    &, & .v-toolbar__content {
      height: auto !important;
    }

    .v-toolbar__content {
      padding: 0;
    }

    .v-btn.v-btn--icon:last-child {
      margin-right: 0 !important;
    }

    h2 {
      margin-bottom: 0;
    }
    i {
      font-size: 14px;
    }
  }

  .dialog-content {
    padding: 32px 34px;
  }

  .v-dialog__content {

    & .v-dialog {
      -webkit-border-radius: 5px !important;
      -moz-border-radius: 5px !important;
      border-radius: 5px !important;
      background: white;
    }

    & .v-dialog.v-dialog--fullscreen {
      border-radius: 0 !important;
    }
  }

  .v-toolbar {
    .v-btn {
      margin: 0 3px;
    }

    i {
      font-size: 18px;
      cursor: pointer;
      color: $white !important;
    }
  }

  .dialog-form-actions {
    padding-top: 30px;

    .submit {
      margin-bottom: 2px;
    }
  }

  .dialog-form {
    position: relative;

    .close-btn {
      position: absolute;
      top: 18px;
      right: 18px;
    }
  }

  .fullView {
    max-width: 99% !important;
  }
</style>
